import React from "react";
import classnames from "classnames";

import prefersReducedMotion from "motion/prefers-reduced-motion";

import GeneLogo from "../../assets/img/branding/GeneFlou_Logo.png";
import GeneWordmark from "../../assets/img/branding/GeneFlou_Wordmark.png";

// The resting image has to match whatever the animation draws, since it is what
// stands in before the player arrives and for anyone who never sees a draw — a
// lockup here would jump to a bare wordmark mid-draw. GeneFlou_Wordmark.png is
// the lockup cropped to its wordmark's alpha bounds.
const RESTING = {
  full: GeneLogo,
  wordmark: GeneWordmark,
};

// The brand mark: the static logo, and the animation that draws it.
//
// Two earlier attempts at this shipped a GIF and both failed. A looping GIF
// never stops; a one-shot GIF stops for good, because its playhead is part of
// the decoded image — a cached copy paints its final frame and never replays,
// so everyone who came back saw a still. That is not a cache header you can
// tune. Lottie has no such state: the player is constructed on mount and starts
// at frame 0 whatever the HTTP cache did, and `loop={false}` bounds it to one
// pass. The cache is out of the picture entirely, which is the point.
//
// Two ways of asking for it:
//
//   trigger="session"  draws on arrival, once per tab
//   trigger="hover"    draws whenever it is pointed at or focused
//
// The hero uses session and the navbar uses hover. The navbar drew on arrival
// originally; it stopped once the hero took over that job, because two marks
// drawing on one page load read as a glitch. Pointing at it is also the more
// discoverable of the two — a 60px strip gives an arrival animation very little
// room, and most readers would never catch it.
const PLAYED_KEY = "geneBrandMarkPlayed";

// Reading the `sessionStorage` *property* is itself what throws SecurityError
// when the origin has no storage access — cookies blocked for the site, an
// opaque origin — so the `if (window.sessionStorage)` guard used elsewhere in
// this codebase is the throwing expression, not a defence against it. Both
// halves need the try.
const hasPlayed = () => {
  try {
    return window.sessionStorage.getItem(PLAYED_KEY) === "1";
  } catch (err) {
    // Nowhere to record a play means every navigation would look like the first
    // one and replay the animation forever. For a decoration, not drawing is the
    // better failure.
    return true;
  }
};

const rememberPlayed = () => {
  try {
    window.sessionStorage.setItem(PLAYED_KEY, "1");
  } catch (err) {
    // Nothing to do: the mark just draws again in the next tab that can't store.
  }
};

// Most mobile browsers move focus to a link when it is tapped, so binding focus
// straight to a replay would draw — and pull the player over mobile data — on
// exactly the interaction touch is meant to be excluded from. `:focus-visible`
// is the browser's own answer to "was this focus keyboard-driven", so it is what
// decides here rather than a hand-rolled pointerdown/keydown tracker; it also
// gets address-bar, programmatic and autofocus right, which a tracker does not,
// and it keeps a mouse click from replaying the mark it just finished drawing.
const isKeyboardFocus = (element) => {
  try {
    return element.matches(":focus-visible");
  } catch (err) {
    // Pre-2022 Safari and Firefox do not know the selector. Not drawing costs
    // those readers a flourish; the other default would let a tap draw and
    // fetch, which is the thing being prevented.
    return false;
  }
};

// Module scope, so the chunk is requested the first time a run is armed and
// never at all otherwise — a reader who has already seen it, who asked for less
// motion, or who never points at the logo does not pay for the player.
const BrandMarkMotion = React.lazy(() =>
  import(/* webpackChunkName: "brand-mark" */ "./BrandMarkMotion")
);

function BrandMark({ className, alt, trigger = "session", variant = "full" }) {
  const wrapperRef = React.useRef(null);

  // A run counter, not a flag. Re-rendering the same <BrandMarkMotion> does not
  // restart Lottie — autoplay fires on mount — so a boolean that is already true
  // would let the navbar draw once per page and then silently do nothing. The
  // counter is the element's key, so each run is a fresh mount from frame 0.
  // Unmounting on completion would be the other way to get a replay, and it
  // cuts the 180ms fade off at the knees.
  //
  // Decided during the first render rather than in an effect, so there is no
  // frame in which a run has started and is about to be called off.
  const [run, setRun] = React.useState(() =>
    trigger === "session" && !hasPlayed() && !prefersReducedMotion() ? 1 : 0
  );
  const [playing, setPlaying] = React.useState(false);

  // Synchronous, because `playing` is state: a cursor sweep can deliver several
  // enter events before React commits, and the state read would be stale for
  // every one of them but the first.
  const busyRef = React.useRef(false);

  const play = React.useCallback(() => {
    // Asked here rather than where the listeners are bound, because the binding
    // happens once at mount and the preference is not a constant: a reader can
    // turn Reduce Motion on — or off — with this page still open. Gating the
    // binding sampled it once and then kept answering with whatever was true at
    // mount, in both directions. Two idle listeners for a reader who wants no
    // motion is the whole cost, and the chunk is still never fetched, because
    // that is `run` moving and this returns before it does.
    if (prefersReducedMotion()) return;
    if (busyRef.current) return;
    busyRef.current = true;
    setRun((n) => n + 1);
  }, []);

  // Both are read by child effects, so inline arrows would re-run the speed
  // effect and re-register the complete listener on every parent render.
  const handleStart = React.useCallback(() => setPlaying(true), []);
  const handleDone = React.useCallback(() => {
    setPlaying(false);
    busyRef.current = false;
  }, []);

  React.useEffect(() => {
    // Session mode only, and recorded when the run is armed rather than when it
    // finishes: the navbar is mounted per page, so navigating away mid-draw
    // unmounts this, and keying off completion would let a reader who clicked
    // early see it start over on the next page and the one after that.
    //
    // Hover mode never reads or writes storage. The two modes share this key, so
    // a hover that wrote it would burn the hero's one play and the landing page
    // would never draw.
    if (trigger === "session" && run > 0) {
      rememberPlayed();
    }
  }, [trigger, run]);

  React.useEffect(() => {
    if (trigger !== "hover") return undefined;

    // The focusable element is the <a> this sits inside, which is an *ancestor*,
    // so it is the <a> that events target. A handler on the wrapper would never
    // see them: propagation runs between the root and the target, and the
    // wrapper is below the target rather than above it, so it is not on that
    // path in either phase. Bound to the host directly for that reason, and not
    // delegated from further up — `focus` does not bubble at all (`focusin` is
    // the one that does), so an ancestor handler would need the capture phase.
    const host =
      wrapperRef.current.closest("a, button") || wrapperRef.current;

    // Declared in here so the effect owns them and never rebinds on a render.
    // `pointerenter` rather than `mouseenter`: it carries the pointer type, so a
    // tap can be turned away without gating the binding on `(hover: hover)` —
    // that query is false on a touch-primary device even with a keyboard
    // attached, and would take the focus trigger away from hybrid users.
    const onPointerEnter = (event) => {
      if (event.pointerType !== "touch") play();
    };
    const onFocus = (event) => {
      if (isKeyboardFocus(event.currentTarget)) play();
    };

    host.addEventListener("pointerenter", onPointerEnter);
    host.addEventListener("focus", onFocus);
    return () => {
      host.removeEventListener("pointerenter", onPointerEnter);
      host.removeEventListener("focus", onFocus);
    };
  }, [trigger, play]);

  return (
    <div
      ref={wrapperRef}
      className={classnames("brand-mark", `brand-mark--${variant}`, className, {
        "is-animating": playing,
      })}
    >
      {/* Never conditional, and hidden only ever by opacity. It is the box the
          page lays out around, it carries the mark's accessible name for the
          whole draw, and it is what every reader sees who is not watching one. */}
      <img src={RESTING[variant] || RESTING.full} alt={alt} />
      {run > 0 && (
        // `run` decides whether the chunk is ever fetched; `playing` drives the
        // cross-fade. They differ while the chunk is in flight, which is what
        // keeps the static logo up until there is a player to swap to.
        <React.Suspense fallback={null}>
          <BrandMarkMotion
            key={run}
            variant={variant}
            onStart={handleStart}
            onDone={handleDone}
          />
        </React.Suspense>
      )}
    </div>
  );
}

export default BrandMark;
